import { useAuth0 } from 'contexts';
import {
  ContactTypeEnum,
  FileTypes,
  IdDocumentType,
  LocalStorageKey,
} from 'enums';
import { useFetch } from 'hooks';
import jwtDecode from 'jwt-decode';
import {
  Contact,
  DirectorDetails,
  DirectorDetailsResponse,
  DirectorDocument,
  SettlementDetails,
  SettlementDocument,
} from 'types';

interface MerchantProfileFunctions {
  resetGetRequests: () => void;
  submitDirectorDetails: (directorDetails: DirectorDetails) => Promise<any>;
  getDirectorDetails: () => Promise<DirectorDetails>;
  submitDirectorDocument: (directorDocument: DirectorDocument) => Promise<void>;
  submitPrimaryContact: (
    primaryContact: Contact,
    isDirector?: boolean
  ) => Promise<any>;
  getPrimaryContact: () => Promise<{
    directorDetails: DirectorDetails;
    primaryContactDetails: Contact;
  }>;
  submitSettlementDetails: (
    settlementDetails: SettlementDetails
  ) => Promise<any>;
  getSettlementDetails: () => Promise<SettlementDetails>;
  submitSettlementDocument: (
    settlementDocument: SettlementDocument
  ) => Promise<any>;
  getPublicId: () => string;
  getContactFromClaims: () => Contact;
}

export const useMerchantProfile = (): MerchantProfileFunctions => {
  const { get, post, put } = useFetch();
  const { accessToken } = useAuth0();
  let controller = new AbortController();

  let publicId: string = null;

  function resetGetRequests(): void {
    controller.abort();
    controller = new AbortController();
  }

  async function submitDirectorDetails(
    directorDetails: DirectorDetails
  ): Promise<any> {
    const documentType = Object.keys(IdDocumentType).find(
      (key) => IdDocumentType[key] === directorDetails.documentType
    );

    const payload = {
      ...directorDetails,
      documentType,
    };

    const idMatrixCount = sessionStorage.getItem(LocalStorageKey.IdMatrixCount);
    const idCheck =
      idMatrixCount === null ||
      (idMatrixCount !== null && parseInt(idMatrixCount, 10) < 5);

    return put(
      `/${getPublicId()}/director`,
      { ...payload, idCheck },
      controller.signal,
      process.env.REACT_APP_MERCHANT_PROFILE_API_URL
    ).then((res) => {
      if (!res.ok) {
        throw new Error();
      }
      return res.json();
    });
  }

  async function getDirectorDetails(): Promise<DirectorDetails> {
    return get(
      `/${getPublicId()}/director`,
      controller.signal,
      process.env.REACT_APP_MERCHANT_PROFILE_API_URL
    ).then(async (res) => {
      if (!res.ok) {
        throw new Error();
      }
      const response = (await res.json()) as DirectorDetailsResponse;

      const doctype = Object.keys(IdDocumentType).find(
        (key) =>
          Object.keys(IdDocumentType).indexOf(key) ===
          Number(response.documentType)
      );

      const directorDetails = {
        ...response,
        documentType: IdDocumentType[doctype],
        licenseAddress:
          response.streetNumber &&
          `${response.unitNumber ? `${response.unitNumber}/` : ''}${
            response.streetNumber
          } ${response.streetName}, ${response.suburb} ${response.state} ${
            response.postcode
          }, Australia`,
        country: 'Australia',
      };

      return directorDetails;
    });
  }

  async function submitDirectorDocument(
    directorDocument: DirectorDocument
  ): Promise<void> {
    const documentType = Object.keys(IdDocumentType).find(
      (key) => IdDocumentType[key] === directorDocument.documentType
    );

    let directorDocumentId;

    if (!directorDocument.id) {
      const upload = await getUploadUrl(
        FileTypes.identity,
        directorDocument.document.name
      );
      await uploadFile(directorDocument.document, upload.url);
      const notify = await notifyDocumentUploaded(
        FileTypes.identity,
        directorDocument.document.name,
        documentType
      );
      directorDocumentId = notify.documentId;
    }

    const payload = {
      ...directorDocument,
      id: directorDocument.id ?? directorDocumentId,
      documentType,
    };
    return put(
      `/${getPublicId()}/director-documents`,
      payload,
      controller.signal,
      process.env.REACT_APP_MERCHANT_PROFILE_API_URL
    );
  }

  async function submitPrimaryContact(
    primaryContact: Contact,
    isDirector = false
  ): Promise<any> {
    const payload = {
      ...primaryContact,
      isPrimary: true,
      contactType: isDirector
        ? ContactTypeEnum.director
        : ContactTypeEnum.contact,
    };
    return put(
      `/${getPublicId()}/contact-details`,
      payload,
      controller.signal,
      process.env.REACT_APP_MERCHANT_PROFILE_API_URL
    );
  }

  async function getPrimaryContact(): Promise<{
    directorDetails: DirectorDetails;
    primaryContactDetails: Contact;
  }> {
    return get(
      `/${getPublicId()}/contact-details`,
      controller.signal,
      process.env.REACT_APP_MERCHANT_PROFILE_API_URL
    ).then((res) => {
      if (!res.ok) {
        throw new Error();
      }
      return res.json();
    });
  }

  async function submitSettlementDetails(
    settlementDetails: SettlementDetails
  ): Promise<any> {
    return put(
      `/${getPublicId()}/settlement-details`,
      settlementDetails,
      controller.signal,
      process.env.REACT_APP_MERCHANT_PROFILE_API_URL
    );
  }

  async function getSettlementDetails(): Promise<SettlementDetails> {
    return get(
      `/${getPublicId()}/settlement-details`,
      controller.signal,
      process.env.REACT_APP_MERCHANT_PROFILE_API_URL
    ).then((res) => {
      if (!res.ok) {
        throw new Error();
      }
      return res.json() as SettlementDetails;
    });
  }

  async function submitSettlementDocument(
    settlementDocument: SettlementDocument
  ): Promise<any> {
    const result = await getUploadUrl(
      FileTypes.bank,
      settlementDocument.bankStatement.name
    );

    await uploadFile(settlementDocument.bankStatement, result.url);
    await notifyDocumentUploaded(
      FileTypes.bank,
      settlementDocument.bankStatement.name,
      'BankStatement'
    );

    return put(
      `/${getPublicId()}/settlement-document`,
      settlementDocument,
      controller.signal,
      process.env.REACT_APP_MERCHANT_PROFILE_API_URL
    );
  }

  function getPublicId(): string {
    if (!publicId) {
      publicId = getPublicIdClaim();
    }

    return publicId;
  }

  function getPublicIdClaim(): string {
    const claims = jwtDecode(accessToken);
    if (!claims) {
      return null;
    }

    return claims[getClaimKey('publicId')];
  }

  function getContactFromClaims(): Contact {
    if (!accessToken) {
      return null;
    }
    const claims = jwtDecode(accessToken);

    const claimsContact: Contact = {
      fullName: `${claims[getClaimKey('firstName')]} ${
        claims[getClaimKey('lastName')]
      }`,
      contactNumber: claims[getClaimKey('phoneNumber')],
      contactEmail: claims[getClaimKey('email')],
    };

    return claimsContact;
  }

  function getClaimKey(key: string): string {
    return `${process.env.REACT_APP_AUTH0_AUDIENCE}${key}`;
  }

  async function getUploadUrl(
    fileType: FileTypes,
    filename: string
  ): Promise<any> {
    return get(
      `/${getPublicId()}/${fileType}-documents/${filename}/upload-url`,
      controller.signal,
      process.env.REACT_APP_MERCHANT_PROFILE_API_URL
    ).then((res) => {
      if (!res.ok) {
        throw new Error();
      }
      return res.json();
    });
  }

  async function uploadFile(file: File, url: string): Promise<any> {
    return fetch(url, {
      body: file,
      method: 'PUT',
      signal: controller.signal,
    }).catch((err) => {
      throw err;
    });
  }

  async function notifyDocumentUploaded(
    fileType: FileTypes,
    filename: string,
    documentType: string
  ): Promise<any> {
    const payload = {
      filename,
      documentType,
    };

    return post(
      `/${getPublicId()}/${fileType}-documents/`,
      payload,
      controller.signal,
      process.env.REACT_APP_MERCHANT_PROFILE_API_URL
    ).then((res) => {
      if (!res.ok) {
        throw new Error();
      }
      return res.json();
    });
  }

  return {
    resetGetRequests,
    submitDirectorDetails,
    getDirectorDetails,
    submitDirectorDocument,
    submitPrimaryContact,
    getPrimaryContact,
    submitSettlementDetails,
    getSettlementDetails,
    submitSettlementDocument,
    getPublicId,
    getContactFromClaims,
  };
};

export default useMerchantProfile;
